// Import required modules and styles
import React, { useState, useRef } from 'react';
import DatePicker, { CalendarContainer } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import 'tailwindcss/tailwind.css'; // Make sure to import Tailwind CSS
import calenderIcon from 'src/assets/icons/Calendar-icon.svg';
import { months } from 'src/constants';
import { twMerge } from 'tailwind-merge';
import crossIcon from 'src/assets/icons/cross-icon.svg';
import dropdownIcon from 'src/assets/images/arrow-dark.svg';

export interface IDatePickerProp {
  customInput?: React.ReactNode;
  currentDate?: any;
  onChange?: (date: Date) => void;
  variant: 'icon' | 'input' | 'range';
  containerClass?: string;
  containerInnerClass?: string;
  minDate?: Date;
  maxDate?: Date;
  onBlur?: (e: React.FocusEvent<any, Element>) => void;
  disabled?: boolean;
  handleClear?: () => void;
  showClearButton?: boolean;
  isReadOnly?: boolean;
  selectedDaysOnly?: any;
  showMonthYearPicker?: boolean;
}

const MyContainer = ({ className, children }: any) => {
  return (
    <CalendarContainer className={className}>
      <div className="relative bg-white">{children}</div>
    </CalendarContainer>
  );
};

const Index = ({
  customInput,
  currentDate,
  onChange,
  variant,
  containerClass,
  containerInnerClass,
  minDate,
  maxDate,
  onBlur,
  disabled,
  handleClear,
  showClearButton = false,
  isReadOnly,
  selectedDaysOnly,
  showMonthYearPicker,
}: IDatePickerProp) => {
  const [date, setDate] = useState<any>();
  const dateWrapperRef = useRef<any>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const onDateSelectHandler = (date: Date) => {
    onChange && onChange(date);
  };

  const getMonthName = (date: Date = new Date()): string => {
    let monthName = months[date.getMonth()];
    return monthName ? monthName.slice(0, 3) : '';
  };

  const disabledStyle = disabled
    ? 'cursor-not-allowed border-zinc-200 hover:border-zinc-200 bg-slate-100 font-normal text-opacity-50 text-blue-950 placeholder:text-opacity-50'
    : '';

  let containerMergeClasses = twMerge(
    'flex w-fit gap-3 hover:border-borderBlue',
    containerClass ?? containerClass,
    disabledStyle
  );

  let containerInnerMergeClasses = twMerge(
    variant === 'input'
      ? 'font-SFProDisplay bg-white rounded-full cursor-pointer date-picker-with-input-variant w-full'
      : variant === 'range'
      ? 'bg-[#f2f2f2] flex gap-1 px-4 py-1 rounded-[10px] w-full date-picker-with-input-variant'
      : '',
    containerInnerClass ?? containerInnerClass,
    disabledStyle
  );

  const filterWeekdays = (date: any, allowedWeekdays: any) => {
    const dayIndex = date.getDay();
    return allowedWeekdays?.includes(dayIndex);
  };

  function handleClickDatePickerIcon(isOpen = true) {
    const datepickerElement = dateWrapperRef.current;
    datepickerElement.setFocus(isOpen);
  }

  return (
    <div className={containerMergeClasses}>
      <div
        className={`relative ${containerInnerMergeClasses}`}
        onClick={() => handleClickDatePickerIcon()}
      >
        <DatePicker
          ref={dateWrapperRef}
          showMonthYearPicker={showMonthYearPicker || false}
          disabled={disabled}
          readOnly={isReadOnly}
          selected={currentDate}
          filterDate={
            selectedDaysOnly
              ? (date) => filterWeekdays(date, selectedDaysOnly)
              : undefined
          }
          onChange={(date) => {
            onDateSelectHandler(date as Date);
            handleClickDatePickerIcon(false);
            setIsDropdownOpen(false);
          }}
          onClickOutside={() => setIsDropdownOpen(false)}
          onFocus={() => setIsDropdownOpen(true)}
          calendarContainer={MyContainer}
          dateFormat={variant === 'input' ? 'MM/dd/yy' : undefined}
          customInput={
            variant === 'input' || variant === 'range' ? undefined : (
              <div className="relative">
                {customInput ? (
                  customInput
                ) : (
                  <>
                    {variant === 'icon' && (
                      <div className="flex items-center gap-4 cursor-pointer">
                        {currentDate && (
                          <p className="text-blue-950 font-SFProDisplay text-lg ">
                            {`${getMonthName(
                              currentDate
                            )} ${currentDate.getFullYear()}`}
                          </p>
                        )}
                        <div className="bg-white w-fit p-[9px] rounded-lg">
                          <img src={calenderIcon} alt="calender icon" />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )
          }
          className={twMerge(
            'custom-date-picker block font-SFProDisplay text-blue-950 text-sm font-medium w-full h-9 pt-[5px] pb-2 placeholder:font-normal placeholder:text-sm placeholder:text-slate-500 justify-start items-center gap-2.5 ',
            disabledStyle
          )}
          calendarClassName="shadow border-0 rounded-lg custom-date-picker__calendar font-SFProDisplay "
          fixedHeight
          placeholderText="Select Date"
          minDate={minDate}
          maxDate={maxDate}
          popperProps={{ strategy: 'fixed' }}
        />
        {showClearButton && currentDate && (
          <div className="absolute h-full right-10 top-1/3 bg-transparent">
            <img
              src={crossIcon}
              alt=""
              className="cursor-pointer"
              onClick={handleClear}
            />
          </div>
        )}
        <div className="absolute h-full right-1 top-1/3 bg-transparent">
          <img
            src={dropdownIcon}
            alt="dropdown icon"
            className={`transform transition-transform h-[8px] mr-[10px] duration-300 ease-in-out  ${
              isDropdownOpen ? 'rotate-180' : 'rotate-0'
            }`}
          />
        </div>
      </div>
    </div>
  );
};

export default Index;
